import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';

import { formatMinor, formatMoneyExact } from '../../Shared/Formatters';

/**
 * Computed-vs-legacy variance.
 *
 * ADMIN ONLY. The backend strips `reconciliation` for tenant callers; this
 * component must never be rendered on a tenant-facing surface, and the figure
 * is deliberately absent from the PDF — a tenant should not read internal notes
 * about which of our two calculations we trust.
 *
 * A non-zero variance is EXPECTED, not an error. The stored
 * salestransactions.revenue_share_amount is defective (Method1 adds the full
 * annual base rent on every daily row; Method2/4/5 bill the surplus when
 * positive but the base rent when negative), which is exactly why invoices
 * recompute from raw sales.
 */
export default function ReconciliationCard({ invoice }) {
    const { t } = useTranslation();
    const rec = invoice?.reconciliation;
    if (!rec) return null;

    const variance = rec.variance_minor || 0;
    const cur = invoice.currency;
    const material = Math.abs(variance) > 100; // more than one major unit

    return (
        <div className="v3-inv-card">
            <h2 className="v3-inv-card-title">{t('Reconciliation vs Revenue Share Report')}</h2>

            <div className="v3-inv-meta-grid">
                <div>
                    <div className="v3-inv-kv-label">{t('Net sales in period')}</div>
                    <div className="v3-inv-kv-value">{formatMoneyExact(rec.net_sales, cur)}</div>
                </div>
                <div>
                    <div className="v3-inv-kv-label">{t('Rent computed here')}</div>
                    <div className="v3-inv-kv-value">{formatMinor(rec.computed_rent_minor, cur)}</div>
                </div>
                <div>
                    <div className="v3-inv-kv-label">{t('Legacy stored value')}</div>
                    <div className="v3-inv-kv-value">{formatMinor(rec.legacy_revenue_share_sum_minor, cur)}</div>
                </div>
                <div>
                    <div className="v3-inv-kv-label">{t('Variance')}</div>
                    <div
                        className="v3-inv-kv-value"
                        style={{ color: material ? 'var(--v3-amber)' : 'var(--v3-text-primary)' }}
                    >
                        {variance > 0 ? '+' : ''}{formatMinor(variance, cur)}
                    </div>
                </div>
            </div>

            {rec.legacy_method_defect && (
                <div className="v3-inv-banner v3-inv-banner-info" style={{ marginTop: 14, marginBottom: 0 }}>
                    <span>{rec.legacy_method_defect}</span>
                </div>
            )}

            {invoice.lease_snapshot?.integrity_warning && (
                <div className="v3-inv-banner v3-inv-banner-warn" style={{ marginTop: 10, marginBottom: 0 }}>
                    <AlertTriangle size={16} />
                    <span>{invoice.lease_snapshot.integrity_warning}</span>
                </div>
            )}
        </div>
    );
}
